/**
 * L'IMMAGINE DA CONDIVIDERE: la giocata (o la prenotazione) disegnata come
 * un pezzo di rotolo, da mandare con «Condividi foto».
 *
 * DUE PARTI, di proposito:
 *   - impagina / impaginaPrenotazione decidono COSA va DOVE: tornano un
 *     elenco di operazioni ({t:'testo'|'linea'|'fascia'|'filigrana', ...})
 *     con le coordinate gia' fatte, su una larghezza di 640 punti. Non
 *     toccano nessun canvas: la misura dei testi la passa chi chiama, e le
 *     prove ne passano una finta a larghezza fissa;
 *   - disegna e inBlob eseguono quelle operazioni su un canvas vero e ne
 *     fanno un PNG.
 *
 * COSA C'E': il dominio scritto (niente logo), il tipo, gli eventi con le
 * quote, la quota totale; posta e vincita solo se chi condivide lo sceglie;
 * il codice della prenotazione «Gioca anche tu» se c'e'; in fondo «Immagine
 * non valida come ricevuta». COSA NON C'E': il codice della giocata, il
 * cliente, il numero di copia. E nemmeno un QR: servirebbe una libreria in
 * piu', e il codice scritto basta.
 *
 * IL LIMITE DI iOS: Safari rifiuta un canvas di piu' di 16.777.216 punti e
 * da' un'immagine vuota. Per questo gli eventi si fermano a 40 («… e altri N
 * eventi») e la scala scende da 2 a 1,5 a 1 finche' il disegno ci sta. Da
 * provare su un iPhone vero con una giocata di 30 eventi prima di
 * annunciarlo: qui non c'e' un WebKit.
 */
import i18next from 'i18next';
import {formattaOra, formattaImporto, mostraQuota} from './formato';
import {fuso as fusoDiChiGuarda, lingua} from '../preferenze';
import {skinCorrente, valuta} from '../skin';
import {datiPrenotazione, maiuscole} from './stampaGiocata';

const t = (chiave, valori) => i18next.t(chiave, valori);

export const LARGHEZZA = 640;
export const PUNTI_MASSIMI = 16777216;
export const EVENTI_MASSIMI = 40;

const MARGINE = 32;
const LARGO = LARGHEZZA - 2 * MARGINE;
const FAMIGLIA = '"Helvetica Neue", Arial, sans-serif';
const font = (peso, px) => `${peso} ${px}px ${FAMIGLIA}`;

const F = {
  testa: font(700, 22),
  fascia: font(800, 24),
  su: font(700, 18),
  nome: font(800, 22),
  mercato: font(400, 18),
  giu: font(800, 20),
  riga: font(800, 22),
  nota: font(400, 15),
  piede: font(400, 16),
  codice: font(800, 34),
  invito: font(400, 20),
  filigrana: font(800, 120),
};

/* Senza un canvas per misurare: una stima, mezza volta e poco piu' la
   grandezza del carattere per lettera. Serve solo a non rompersi; chi disegna
   davvero passa la misura del canvas (misuraCanvas). */
function misuraStimata(testo, f) {
  const px = Number((String(f).match(/(\d+(?:\.\d+)?)px/) || [])[1]) || 16;
  return String(testo || '').length * px * 0.56;
}

function altezzaRiga(f) {
  const px = Number((String(f).match(/(\d+(?:\.\d+)?)px/) || [])[1]) || 16;
  return Math.round(px * 1.3);
}

/** Un testo spezzato in righe che stanno in `larghezza`. Una parola troppo lunga si spezza a lettere. */
function aCapo(testo, f, larghezza, misura) {
  const parole = String(testo || '').split(/\s+/).filter(Boolean);
  const righe = [];
  let corrente = '';
  const spezza = (parola) => {
    let pezzo = '';
    for (const c of parola) {
      if (pezzo && misura(pezzo + c, f) > larghezza) {
        righe.push(pezzo);
        pezzo = c;
      } else {
        pezzo += c;
      }
    }
    return pezzo;
  };
  parole.forEach((p) => {
    const prova = corrente ? `${corrente} ${p}` : p;
    if (misura(prova, f) <= larghezza) {
      corrente = prova;
      return;
    }
    if (corrente) righe.push(corrente);
    corrente = misura(p, f) > larghezza ? spezza(p) : p;
  });
  if (corrente) righe.push(corrente);
  return righe.length ? righe : [''];
}

/* Chi compone: tiene la y e aggiunge operazioni. */
function impaginatore(misura) {
  const ops = [];
  let y = MARGINE;

  const testo = (s, f, {allinea = 'sinistra', colore, x} = {}) => {
    const righe = aCapo(s, f, LARGO, misura);
    const h = altezzaRiga(f);
    righe.forEach((r) => {
      y += h;
      const dove = x != null ? x
        : (allinea === 'destra' ? MARGINE + LARGO : (allinea === 'centro' ? LARGHEZZA / 2 : MARGINE));
      ops.push({t: 'testo', x: dove, y: y - Math.round(h * 0.25), testo: r, font: f, allinea, ...(colore ? {colore} : {})});
    });
  };

  /* Una riga a due colonne: a sinistra si va a capo, a destra no. */
  const due = (sinistra, destra, f, fDestra = f) => {
    const h = Math.max(altezzaRiga(f), altezzaRiga(fDestra));
    const lDestra = destra ? misura(destra, fDestra) : 0;
    const spazio = Math.max(LARGO * 0.5, LARGO - lDestra - 16);
    const righe = aCapo(sinistra, f, spazio, misura);
    righe.forEach((r, i) => {
      y += h;
      const base = y - Math.round(h * 0.25);
      ops.push({t: 'testo', x: MARGINE, y: base, testo: r, font: f, allinea: 'sinistra'});
      if (i === 0 && destra) {
        ops.push({t: 'testo', x: MARGINE + LARGO, y: base, testo: destra, font: fDestra, allinea: 'destra'});
      }
    });
  };

  const fascia = (s, f = F.fascia) => {
    const h = altezzaRiga(f) + 16;
    y += 8;
    ops.push({t: 'fascia', x: MARGINE, y, larghezza: LARGO, altezza: h});
    ops.push({t: 'testo', x: LARGHEZZA / 2, y: y + h - 14, testo: s, font: f, allinea: 'centro', colore: '#ffffff'});
    y += h;
  };

  const linea = (spessore = 1, tratteggio) => {
    y += 8;
    ops.push({t: 'linea', x1: MARGINE, y1: y, x2: MARGINE + LARGO, y2: y, spessore, ...(tratteggio ? {tratteggio} : {})});
    y += 2;
  };

  const spazio = (h) => { y += h; };

  /* Un riquadro intorno a quello che disegna `dentro` (quattro linee). */
  const riquadro = (dentro) => {
    y += 10;
    const sopra = y;
    y += 6;
    dentro();
    y += 14;
    const sotto = y;
    const x1 = MARGINE;
    const x2 = MARGINE + LARGO;
    ops.push({t: 'linea', x1, y1: sopra, x2, y2: sopra, spessore: 2});
    ops.push({t: 'linea', x1, y1: sotto, x2, y2: sotto, spessore: 2});
    ops.push({t: 'linea', x1, y1: sopra, x2: x1, y2: sotto, spessore: 2});
    ops.push({t: 'linea', x1: x2, y1: sopra, x2, y2: sotto, spessore: 2});
  };

  const fine = () => {
    const altezza = y + MARGINE;
    /* La filigrana a meta' altezza, sotto a tutto il resto. */
    ops.unshift({t: 'filigrana', x: LARGHEZZA / 2, y: Math.round(altezza * 0.45), testo: maiuscole(t('Copia')),
      font: F.filigrana, angolo: -32 * Math.PI / 180});
    return {larghezza: LARGHEZZA, altezza, ops};
  };

  return {testo, due, fascia, linea, spazio, riquadro, fine};
}

function scriviEventi(n) {
  return n === 1 ? t('1 evento') : t('{{n}} eventi', {n});
}

function eAltri(n) {
  return n === 1 ? t('… e un altro evento') : t('… e altri {{n}} eventi', {n});
}

function siglaDi(zona) {
  try {
    const p = new Intl.DateTimeFormat(lingua(), {timeZone: zona, timeZoneName: 'short'})
        .formatToParts(new Date()).find((x) => x.type === 'timeZoneName');
    return p ? p.value : '';
  } catch (e) {
    return '';
  }
}

function notaOrari(zona) {
  const sigla = siglaDi(zona);
  return sigla ? t('Orari nel fuso {{fuso}} ({{sigla}}).', {fuso: zona, sigla}) : t('Orari nel fuso {{fuso}}.', {fuso: zona});
}

function eventi(p, lista, zona, conSegnoAccanto) {
  const mostrati = lista.slice(0, EVENTI_MASSIMI);
  mostrati.forEach((e, i) => {
    if (i > 0) p.linea(1, [6, 4]);
    p.spazio(4);
    p.due(e.competizione || e.numero || '—', formattaOra(e.inizio, 'completa', {fuso: zona}), F.su);
    p.testo([e.numero, e.evento].filter(Boolean).join(' · ') || '—', F.nome);
    if (conSegnoAccanto) {
      p.due(`${e.mercato} · ${e.segno}`, e.quota, F.mercato, F.giu);
    } else {
      p.testo(e.mercato, F.mercato);
      p.due(t('Segno: {{segno}}', {segno: e.segno}), t('Quota: {{quota}}', {quota: e.quota}), F.giu);
    }
  });
  if (lista.length > EVENTI_MASSIMI) {
    p.linea(1, [6, 4]);
    p.testo(eAltri(lista.length - EVENTI_MASSIMI), F.su);
  }
  p.spazio(4);
  p.testo(notaOrari(zona), F.nota);
}

function piede(p) {
  p.spazio(10);
  p.testo(t('18+ · Il gioco può causare dipendenza. Immagine non valida come ricevuta.'), F.piede, {allinea: 'centro'});
}

/**
 * La giocata da condividere, impaginata.
 *
 * @param dati    la giocata come la da' datiStampa (stampaGiocata.js)
 * @param importi true per disegnare posta e vincita
 * @param codicePrenotazione il codice «Gioca anche tu», se c'e'
 * @param misura  (testo, font) => larghezza in punti; di serie una stima
 * @returns {{larghezza: number, altezza: number, ops: Array}}
 */
export function impagina(dati, {importi = false, codicePrenotazione = '', misura = misuraStimata, skin, fuso} = {}) {
  const d = dati || {eventi: []};
  const s = skin || skinCorrente() || {};
  const zona = fuso || fusoDiChiGuarda();
  const codiceValuta = s.currency || valuta();
  const soldi = (n) => formattaImporto(n, {valuta: codiceValuta});
  const p = impaginatore(misura);

  p.due(s.domain || s.name || '', d.statoNome || '', F.testa);
  p.fascia(`${maiuscole(d.tipoNome || '')} · ${maiuscole(scriviEventi(d.quanteEventi || (d.eventi || []).length))}`);
  eventi(p, d.eventi || [], zona, false);

  p.linea(2);
  const min = d.quotaMin != null ? d.quotaMin : d.quotaMax;
  const max = d.quotaMax != null ? d.quotaMax : d.quotaMin;
  if (min != null) {
    p.spazio(4);
    if (min === max) p.due(maiuscole(t('Quota totale')), mostraQuota(min), F.riga);
    else p.due(maiuscole(t('Quota min / max')), `${mostraQuota(min)} / ${mostraQuota(max)}`, F.riga);
  }
  if (importi) {
    p.spazio(4);
    p.due(t('Puntata'), soldi(d.posta), F.riga);
    const vincita = d.vincitaMax != null && d.vincitaMax === d.vincitaMin
      ? soldi(d.vincitaMax)
      : `${soldi(d.vincitaMin)} – ${d.vincitaMax == null ? '—' : soldi(d.vincitaMax)}`;
    p.due(maiuscole(t('Vincita potenziale')), vincita, F.riga);
    if (d.seVinconoTutte != null) p.due(t('Se vincono tutte le combinazioni'), soldi(d.seVinconoTutte), F.su);
  }
  if (codicePrenotazione) {
    p.riquadro(() => {
      p.testo(t('Gioca anche tu con il codice'), F.invito, {allinea: 'centro'});
      p.testo(codicePrenotazione, F.codice, {allinea: 'centro'});
    });
  }
  piede(p);
  return p.fine();
}

/**
 * La prenotazione da condividere, impaginata: il codice, gli eventi, fino a
 * quando vale, e la stessa riga in fondo.
 */
export function impaginaPrenotazione(prenotazione, {misura = misuraStimata, skin, fuso} = {}) {
  const d = datiPrenotazione(prenotazione);
  const s = skin || skinCorrente() || {};
  const zona = fuso || fusoDiChiGuarda();
  const p = impaginatore(misura);

  p.due(s.domain || s.name || '', '', F.testa);
  p.fascia(maiuscole(t('Prenotazione')));
  p.spazio(6);
  p.testo(t('Non è una giocata: si gioca col codice, alle quote del momento.'), F.mercato, {allinea: 'centro'});
  p.riquadro(() => {
    p.testo(t('Gioca anche tu con il codice'), F.invito, {allinea: 'centro'});
    p.testo(d.codice, F.codice, {allinea: 'centro'});
    if (d.scadeIl) {
      p.testo(t('Vale fino al {{quando}}', {quando: formattaOra(d.scadeIl, 'completa', {fuso: zona, sigla: true})}),
          F.su, {allinea: 'centro'});
    }
  });
  p.fascia(`${maiuscole(d.tipoNome)} · ${maiuscole(scriviEventi(d.quante))}`);
  eventi(p, d.eventi, zona, true);
  p.linea(2);
  /* POSTA E VINCITA anche sull'immagine: sono la proposta stessa, come nel
     messaggio e sulla carta. Senza importo (prenotazione dal «Gioca anche
     tu») la riga non c'e'. */
  if (d.quota != null || d.importo != null) {
    const codiceValuta = s.currency || valuta();
    const soldi = (n) => formattaImporto(n, {valuta: codiceValuta});
    p.spazio(4);
    if (d.quota != null) p.due(maiuscole(t('Quota totale')), mostraQuota(d.quota), F.riga);
    if (d.importo != null) p.due(t('Importo proposto'), soldi(d.importo), F.riga);
    if (d.vincita != null) p.due(maiuscole(t('Vincita indicativa')), soldi(d.vincita), F.riga);
  }
  piede(p);
  return p.fine();
}

// ---------------------------------------------------------------------------
// Il disegno
// ---------------------------------------------------------------------------

/**
 * La scala piu' alta (2, poi 1,5, poi 1) con cui il disegno sta sotto i
 * 16.777.216 punti di iOS; se nemmeno 1 basta, quella che ci sta.
 */
export function scalaPer(larghezza, altezza, scala = 2) {
  const ci = (s) => Math.ceil(larghezza * s) * Math.ceil(altezza * s) <= PUNTI_MASSIMI;
  const prove = [scala, 1.5, 1].filter((s) => s <= scala);
  for (const s of prove) if (ci(s)) return s;
  let s = Math.floor(Math.sqrt(PUNTI_MASSIMI / Math.max(1, larghezza * altezza)) * 100) / 100;
  while (s > 0.01 && !ci(s)) s -= 0.01;
  return Math.max(0.01, s);
}

const ALLINEA = {sinistra: 'left', destra: 'right', centro: 'center'};

/**
 * Esegue un'impaginazione su un canvas. Torna la scala usata.
 */
export function disegna(canvas, layout, scala = 2) {
  const s = scalaPer(layout.larghezza, layout.altezza, scala);
  canvas.width = Math.ceil(layout.larghezza * s);
  canvas.height = Math.ceil(layout.altezza * s);
  const g = canvas.getContext('2d');
  if (!g) return s;
  g.setTransform(s, 0, 0, s, 0, 0);
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, layout.larghezza, layout.altezza);
  g.textBaseline = 'alphabetic';
  layout.ops.forEach((op) => {
    if (op.t === 'testo') {
      g.font = op.font;
      g.fillStyle = op.colore || '#000000';
      g.textAlign = ALLINEA[op.allinea] || 'left';
      g.fillText(op.testo, op.x, op.y);
    } else if (op.t === 'linea') {
      g.strokeStyle = op.colore || '#000000';
      g.lineWidth = op.spessore || 1;
      g.setLineDash(op.tratteggio || []);
      g.beginPath();
      g.moveTo(op.x1, op.y1);
      g.lineTo(op.x2, op.y2);
      g.stroke();
      g.setLineDash([]);
    } else if (op.t === 'fascia') {
      g.fillStyle = op.colore || '#000000';
      g.fillRect(op.x, op.y, op.larghezza, op.altezza);
    } else if (op.t === 'filigrana') {
      g.save();
      g.translate(op.x, op.y);
      g.rotate(op.angolo || 0);
      g.font = op.font;
      g.fillStyle = 'rgba(0, 0, 0, 0.07)';
      g.textAlign = 'center';
      g.fillText(op.testo, 0, 0);
      g.restore();
    }
  });
  return s;
}

/** Il canvas in un PNG; null se il browser non ce la fa (il canvas troppo grande di iOS). */
export function inBlob(canvas) {
  return new Promise((risolvi) => {
    try {
      canvas.toBlob((b) => risolvi(b || null), 'image/png');
    } catch (e) {
      risolvi(null);
    }
  });
}

/** La misura dei testi di un canvas vero, per impagina. */
export function misuraCanvas(canvas) {
  const g = canvas.getContext('2d');
  return (testo, f) => {
    g.font = f;
    return g.measureText(String(testo || '')).width;
  };
}

function inUnCanvas(componi) {
  if (typeof document === 'undefined') return Promise.resolve(null);
  const canvas = document.createElement('canvas');
  if (!canvas.getContext || !canvas.getContext('2d')) return Promise.resolve(null);
  const layout = componi(misuraCanvas(canvas));
  disegna(canvas, layout);
  return inBlob(canvas);
}

/** La giocata in un PNG, tutto in uno: per «Condividi foto». */
export function creaImmagineGiocata(dati, {importi = false, codicePrenotazione = ''} = {}) {
  return inUnCanvas((misura) => impagina(dati, {importi, codicePrenotazione, misura}));
}

/** La prenotazione in un PNG. */
export function creaImmaginePrenotazione(prenotazione) {
  return inUnCanvas((misura) => impaginaPrenotazione(prenotazione, {misura}));
}
